/**
 * After a deploy, an open tab still asks for the old build's screen files, which are gone. That shows up as a
 * failed dynamic import (or Vite's `vite:preloadError`). The fix is a reload, which fetches the new build.
 *
 * We reload once, and remember when in sessionStorage. Within the cooldown we don't reload again, so a file that
 * is missing for some other reason can't loop the tab; the screen's error boundary shows "A new version is out"
 * with a Reload button instead.
 */
import type { ReportContext } from './reportError'

export const RELOAD_KEY = 'app.staleChunkReloadAt'
export const RELOAD_COOLDOWN_MS = 5 * 60_000

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS|ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  if (error instanceof Error) return error.name === 'ChunkLoadError' || CHUNK_ERROR.test(error.message)
  return CHUNK_ERROR.test(String(error))
}

type Store = Pick<Storage, 'getItem' | 'setItem'>

/** True, and the time is recorded, if no reload happened within the cooldown. False when storage is unavailable. */
export function claimReload(storage: Store | undefined, now: number, cooldownMs = RELOAD_COOLDOWN_MS): boolean {
  if (!storage) return false
  try {
    const last = Number(storage.getItem(RELOAD_KEY))
    if (Number.isFinite(last) && last > 0 && now - last < cooldownMs) return false
    storage.setItem(RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

export interface RecoveryDeps {
  storage: Store | undefined
  now: () => number
  reload: () => void
  report: (error: unknown, context: ReportContext) => Promise<void> | undefined
  cooldownMs?: number
  /** How long to wait for the report to leave before reloading. */
  reportWaitMs?: number
}

let reloading = false

/** Whether a stale-chunk reload is on its way in this tab. The boundary shows "Loading the new version" then. */
export function isReloading(): boolean {
  return reloading
}

/**
 * Reports the stale file and, once per cooldown, reloads the tab. Returns true when a reload is coming. Synchronous
 * so an event listener can decide whether to call preventDefault().
 */
export function recoverFromStaleChunk(error: unknown, deps: RecoveryDeps): boolean {
  if (reloading) return true
  const claimed = claimReload(deps.storage, deps.now(), deps.cooldownMs)
  const sent = deps.report(error, { kind: 'stale-chunk' })
  if (!claimed) return false
  reloading = true
  const wait = new Promise<void>((resolve) => setTimeout(resolve, deps.reportWaitMs ?? 400))
  void Promise.race([sent ?? Promise.resolve(), wait]).finally(() => deps.reload())
  return true
}

/** Listens for stale-chunk failures that happen outside React (Vite's preload, unhandled import rejections). */
export function installChunkRecovery(target: EventTarget, deps: RecoveryDeps): () => void {
  const onPreloadError = (event: Event) => {
    const error = (event as Event & { payload?: unknown }).payload
    // preventDefault stops Vite from throwing the error, but only when we are about to reload anyway.
    if (recoverFromStaleChunk(error, deps)) event.preventDefault()
  }
  const onRejection = (event: Event) => {
    const reason = (event as Event & { reason?: unknown }).reason
    if (isChunkLoadError(reason) && recoverFromStaleChunk(reason, deps)) event.preventDefault()
  }
  target.addEventListener('vite:preloadError', onPreloadError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('vite:preloadError', onPreloadError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}

/** Tests only: forget a pending reload. */
export function resetChunkRecovery() {
  reloading = false
}

/** The real browser: this tab's sessionStorage, the clock, location.reload(). */
export function browserRecovery(report: RecoveryDeps['report']): RecoveryDeps {
  let storage: Store | undefined
  try {
    storage = window.sessionStorage
  } catch {
    storage = undefined // blocked storage: never auto-reload, so never loop
  }
  return { storage, now: () => Date.now(), reload: () => window.location.reload(), report }
}
